import { ApiError, IS_OFFLINE, readThrough, request } from "./http";
import { apiUrl, uploadUrl } from "./config";
import {
  adminOverview,
  adminTrend,
  adminCategoryStats,
  adminRecentActivity,
  adminBusinesses,
  pendingApprovals,
  adminUsers,
  adminLeads,
  adminOrders,
  adminInvoices,
  adminContacts,
  advertisements,
  adPlacements,
  adminSubscriptions,
  adminSubcategories,
  approvalAuditLog,
  adminAuditLog,
  adminBusinessDetails,
} from "@/mocks/adminData";
import { reviews } from "@/mocks/reviews";
import { reviewQueue } from "@/mocks/reviewQueue";
import { locations } from "@/mocks/locations";
import { plans } from "@/mocks/plans";
import { mediaLibraryItems } from "@/mocks/mediaLibrary";
import { marqueeMessages, popupAds } from "@/mocks/announcements";
import type {
  AdminBusiness,
  AdminBusinessDetail,
  AdminCategoryStat,
  AdminLocation,
  AdminOverview,
  AdminTrendPoint,
  AdminUser,
  AdPlacement,
  Advertisement,
  ApprovalAuditEntry,
  Category,
  ContactLead,
  DistrictOption,
  InvoiceRecord,
  Location,
  MarqueeMessage,
  OrderRecord,
  MediaItemPatch,
  MediaLibraryItem,
  MediaLibraryPage,
  Plan,
  PopupAd,
  PopupAdMediaType,
  Review,
  SiteSettings,
  Subcategory,
  Subscription,
} from "@/types";

export interface AdminActivity {
  id: string;
  icon: string;
  tone: string;
  text: string;
  meta: string;
}

function simulate(ms = 320): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export const getAdminOverview = (token: string | null): Promise<AdminOverview> =>
  readThrough<AdminOverview>("/admin/dashboard", adminOverview as AdminOverview, { token });

export const getAdminTrend = (token: string | null): Promise<AdminTrendPoint[]> =>
  readThrough<AdminTrendPoint[]>("/admin/analytics/trend", adminTrend as AdminTrendPoint[], { token });

export const getAdminCategoryStats = (token: string | null): Promise<AdminCategoryStat[]> =>
  readThrough<AdminCategoryStat[]>("/admin/analytics/categories", adminCategoryStats as AdminCategoryStat[], {
    token,
  });

export const getAdminActivity = (token: string | null): Promise<AdminActivity[]> =>
  readThrough<AdminActivity[]>("/admin/activity", adminRecentActivity as AdminActivity[], { token });

export const getAdminBusinesses = (token: string | null): Promise<AdminBusiness[]> =>
  readThrough<AdminBusiness[]>("/admin/businesses", adminBusinesses as AdminBusiness[], { token });

/** Full editable record for the admin business editor. */
export const getAdminBusinessById = (
  id: string,
  token: string | null,
): Promise<AdminBusinessDetail | undefined> =>
  readThrough<AdminBusinessDetail | undefined>(
    `/admin/businesses/${id}`,
    (adminBusinessDetails as AdminBusinessDetail[]).find((b) => b.id === id),
    { token },
  );

/**
 * Persists edits to a business (details + gallery order) from the admin editor.
 * Resolves to success; never throws so the editor keeps its inline state.
 */
export async function saveAdminBusiness(
  id: string,
  payload: Partial<AdminBusinessDetail>,
  token: string | null,
): Promise<boolean> {
  if (IS_OFFLINE) {
    await simulate();
    return true;
  }
  try {
    await request(`/admin/businesses/${id}`, { method: "PUT", token, body: payload });
    return true;
  } catch {
    return false;
  }
}

export const getPendingApprovals = (token: string | null) =>
  readThrough<typeof pendingApprovals>("/admin/approvals", pendingApprovals, { token });

export const getAdminUsers = (token: string | null): Promise<AdminUser[]> =>
  readThrough<AdminUser[]>("/admin/users", adminUsers as AdminUser[], { token });

export const getAdminLeads = (token: string | null): Promise<ContactLead[]> =>
  readThrough<ContactLead[]>("/admin/leads", adminLeads as ContactLead[], { token });

export const getAdminContacts = (token: string | null): Promise<ContactLead[]> =>
  readThrough<ContactLead[]>("/admin/crm/contacts", adminContacts as ContactLead[], { token });

export const getAdminOrders = (token: string | null): Promise<OrderRecord[]> =>
  readThrough<OrderRecord[]>("/admin/crm/orders", adminOrders as OrderRecord[], { token });

export const getAdminInvoices = (token: string | null): Promise<InvoiceRecord[]> =>
  readThrough<InvoiceRecord[]>("/admin/crm/invoices", adminInvoices as InvoiceRecord[], { token });

export const getAdminInvoiceById = (id: string, token: string | null): Promise<InvoiceRecord | undefined> =>
  readThrough<InvoiceRecord | undefined>(
    `/admin/crm/invoices/${id}`,
    (adminInvoices as InvoiceRecord[]).find((i) => i.id === id),
    { token },
  );

export const getAdvertisements = (token: string | null): Promise<Advertisement[]> =>
  readThrough<Advertisement[]>("/admin/advertisements", advertisements as Advertisement[], { token });

export const getAdPlacements = (token: string | null): Promise<AdPlacement[]> =>
  readThrough<AdPlacement[]>("/admin/ad-placements", adPlacements as AdPlacement[], { token });

export const getSubscriptions = (token: string | null): Promise<Subscription[]> =>
  readThrough<Subscription[]>("/admin/subscriptions", adminSubscriptions as Subscription[], { token });

export const getSubcategories = (token: string | null): Promise<Subcategory[]> =>
  readThrough<Subcategory[]>("/admin/subcategories", adminSubcategories as Subcategory[], { token });

export const getApprovalAuditLog = (token: string | null): Promise<ApprovalAuditEntry[]> =>
  readThrough<ApprovalAuditEntry[]>("/admin/approvals/audit", approvalAuditLog as ApprovalAuditEntry[], { token });

/* ---------------- Admin audit trail (activity feed) ---------------- */

/** One moderation action recorded in the admin audit trail. */
export interface AdminAuditEntry {
  id: string;
  entityType: "listing" | "review";
  entityId: number | string;
  entityLabel: string;
  action: string;
  actor: string;
  note: string;
  createdAt: string;
}

/** Who approved / rejected / deleted each listing and review, newest first. */
export const getAdminAuditLog = (token: string | null): Promise<AdminAuditEntry[]> =>
  readThrough<AdminAuditEntry[]>("/admin/audit", adminAuditLog as AdminAuditEntry[], { token });

/* ---------------- Reviews moderation (§13) ---------------- */

// The admin queue is the pending/hidden reviews plus every published review.
const mockAdminReviews: Review[] = [
  ...reviewQueue.map((r) => ({ ...r, status: r.status as Review["status"] })),
  ...(reviews as Review[]).map((r) => ({ ...r, status: "published" as const })),
];

/** Every review on the platform, including those awaiting moderation. */
export const getAdminReviews = (token: string | null): Promise<Review[]> =>
  readThrough<Review[]>("/admin/reviews", mockAdminReviews, { token });

/**
 * Approves, rejects (hides) or permanently removes a review. Returns success;
 * never throws so the console can keep its optimistic row state.
 */
export const moderateReview = (
  id: string,
  action: "approve" | "reject" | "delete",
  token: string | null,
): Promise<boolean> => adminAction("/admin/reviews", { id, action }, token);

/* ---------------- Leads (§14) ---------------- */

/** Persists a lead's workflow status (§14). Returns success; never throws. */
export const updateAdminLeadStatus = (
  id: string,
  status: ContactLead["status"],
  token: string | null,
): Promise<boolean> => adminAction("/admin/leads", { id, status }, token);

/* ---------------- Listing plans (editable pricing page) ---------------- */

/** Every active listing plan, in display order, for the admin editor. */
export const getAdminPlans = (token: string | null): Promise<Plan[]> =>
  readThrough<Plan[]>("/admin/plans", plans as unknown as Plan[], { token });
/**
 * Persists edits to a single plan. Throws on failure so the editor can surface
 * a clear message and roll back its optimistic state.
 */
export async function saveAdminPlan(
  id: string,
  patch: Partial<Plan>,
  token: string | null,
): Promise<void> {
  if (IS_OFFLINE) {
    await simulate();
    return;
  }
  await request(`/admin/plans/${id}`, { method: "PUT", token, body: patch });
}

/* ---------------- Image library (on-demand re-sync) ---------------- */

/**
 * Triggers a full background re-sync of the given Readdy CDN images. The server
 * copies every image that is missing from its own uploads folder and returns how
 * many were queued. Throws on failure so the caller can surface a clear message.
 */
export async function resyncAllImages(urls: string[], token: string | null): Promise<number> {
  if (IS_OFFLINE) {
    await simulate(600);
    return urls.length;
  }
  const res = await request<{ accepted?: number }>("/admin/mirror-images/resync", {
    method: "POST",
    token,
    body: { urls },
  });
  return typeof res?.accepted === "number" ? res.accepted : 0;
}

/* ---------------- Media library (site-wide, WordPress-style) ---------------- */

/** Query accepted by the media library list. */
export interface MediaQuery {
  search?: string;
  folder?: string;
  source?: string;
  page?: number;
  limit?: number;
}

const MEDIA_PAGE_SIZE = 24;

function formatBytes(bytes: number): string {
  if (!bytes || bytes < 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Normalises a numeric id from the API to the string ids the UI compares on. */
function toMediaItem(raw: MediaLibraryItem): MediaLibraryItem {
  return { ...raw, id: String(raw.id) };
}

/** Offline-only: filters, paginates and summarises the demo library. */
function buildDemoMediaPage(query: MediaQuery): MediaLibraryPage {
  const q = (query.search || "").toLowerCase().trim();
  const limit = query.limit || MEDIA_PAGE_SIZE;
  const page = query.page || 1;

  const filtered = mediaLibraryItems.filter((item) => {
    const matchesFolder = !query.folder || query.folder === "all" || item.folder === query.folder;
    const matchesSource = !query.source || query.source === "all" || item.source === query.source;
    const haystack = [item.title, item.originalName, item.fileName, item.altText, item.caption];
    const matchesQuery = !q || haystack.some((value) => (value || "").toLowerCase().includes(q));
    return matchesFolder && matchesSource && matchesQuery;
  });

  const folderCounts = new Map<string, number>();
  mediaLibraryItems.forEach((item) => folderCounts.set(item.folder, (folderCounts.get(item.folder) || 0) + 1));
  const folders = Array.from(folderCounts.entries())
    .map(([folder, count]) => ({ folder, count }))
    .sort((a, b) => a.folder.localeCompare(b.folder));

  const totalBytes = mediaLibraryItems.reduce((sum, item) => sum + (item.sizeBytes || 0), 0);
  const start = (page - 1) * limit;

  return {
    items: filtered.slice(start, start + limit),
    total: filtered.length,
    page,
    limit,
    folders,
    stats: { total: mediaLibraryItems.length, size: formatBytes(totalBytes) },
  };
}

/** One page of the site-wide media library (search, folder + source filters). */
export async function getAdminMedia(query: MediaQuery, token: string | null): Promise<MediaLibraryPage> {
  const params = new URLSearchParams();
  if (query.search) params.set("search", query.search);
  if (query.folder) params.set("folder", query.folder);
  if (query.source) params.set("source", query.source);
  params.set("page", String(query.page || 1));
  params.set("limit", String(query.limit || MEDIA_PAGE_SIZE));

  const page = await readThrough<MediaLibraryPage>(`/admin/media?${params.toString()}`, buildDemoMediaPage(query), {
    token,
  });
  return { ...page, items: (page.items || []).map(toMediaItem) };
}

/**
 * Uploads one or more images into the library. Posts multipart/form-data to the
 * admin endpoint (field name "files"); the server writes the files under
 * /uploads/library and returns the created records. Throws on failure.
 */
export async function uploadAdminMedia(files: File[], token: string | null): Promise<MediaLibraryItem[]> {
  if (IS_OFFLINE) {
    await simulate(500);
    return files.map((file, index) => ({
      id: `local-${Date.now()}-${index}`,
      url: URL.createObjectURL(file),
      fileName: file.name,
      originalName: file.name,
      title: file.name.replace(/\.[^.]+$/, ""),
      altText: "",
      caption: "",
      folder: "library",
      mime: file.type || "image/jpeg",
      size: formatBytes(file.size),
      sizeBytes: file.size,
      width: null,
      height: null,
      source: "admin",
      uploader: "You",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }));
  }

  const form = new FormData();
  files.forEach((file) => form.append("files", file));

  const res = await fetch(apiUrl("/admin/media"), {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: form,
  });

  const raw = await res.text();
  let parsed: { code?: string; data?: { items?: MediaLibraryItem[] }; meta?: { message?: string } } | null = null;
  try {
    parsed = raw ? (JSON.parse(raw) as typeof parsed) : null;
  } catch {
    parsed = null;
  }

  if (!res.ok || (parsed?.code && parsed.code !== "OK")) {
    throw new ApiError(parsed?.meta?.message || "Upload failed. Please try another image.", res.status);
  }
  return (parsed?.data?.items || []).map(toMediaItem);
}

/** Renames / captions a media item. Throws on failure so the panel can react. */
export async function updateAdminMediaItem(
  id: string,
  patch: MediaItemPatch,
  token: string | null,
): Promise<MediaLibraryItem> {
  if (IS_OFFLINE) {
    await simulate();
    const existing = mediaLibraryItems.find((item) => item.id === id);
    return { ...(existing as MediaLibraryItem), id, ...patch };
  }
  const item = await request<MediaLibraryItem>(`/admin/media/${id}`, { method: "PUT", token, body: patch });
  return toMediaItem(item);
}

/** Deletes one media item (row + file on disk). Resolves to success; never throws. */
export async function deleteAdminMediaItem(id: string, token: string | null): Promise<boolean> {
  if (IS_OFFLINE) {
    await simulate();
    return true;
  }
  try {
    await request(`/admin/media/${id}`, { method: "DELETE", token });
    return true;
  } catch {
    return false;
  }
}

/** Deletes a batch of media items. Throws on failure so the caller can react. */
export async function bulkDeleteAdminMedia(ids: string[], token: string | null): Promise<number> {
  if (IS_OFFLINE) {
    await simulate();
    return ids.length;
  }
  const res = await request<{ removed?: number }>("/admin/media/bulk-delete", {
    method: "POST",
    token,
    body: { ids: ids.map(Number) },
  });
  return res?.removed ?? ids.length;
}

/** Moves a batch of media items into a folder. Throws on failure. */
export async function moveAdminMedia(ids: string[], folder: string, token: string | null): Promise<number> {
  if (IS_OFFLINE) {
    await simulate();
    return ids.length;
  }
  const res = await request<{ moved?: number }>("/admin/media/move", {
    method: "POST",
    token,
    body: { ids: ids.map(Number), folder },
  });
  return res?.moved ?? ids.length;
}

/* ---------------- Site settings (branding + configuration) ---------------- */

/**
 * Persists a partial site-settings patch. Throws on failure so the caller can
 * roll back its optimistic update and surface the error.
 */
export async function saveAdminSiteSettings(
  patch: Partial<SiteSettings>,
  token: string | null,
): Promise<void> {
  if (IS_OFFLINE) {
    await simulate();
    return;
  }
  await request("/admin/settings", { method: "PUT", token, body: patch });
}

/** Lead-alert settings, read from the admin-only settings endpoint. */
export interface AdminAlertSettings {
  alertAdminOnLead: boolean;
  alertOwnerOnEnquiry: boolean;
  leadDigestEnabled: boolean;
  adminAlertRecipients: string;
}

/**
 * Reads the admin settings (which include the lead-alert fields the public
 * endpoint deliberately omits). The generic settings context stays public-only.
 */
export const getAdminSettings = (token: string | null): Promise<AdminAlertSettings> =>
  readThrough<AdminAlertSettings>(
    "/admin/settings",
    {
      alertAdminOnLead: true,
      alertOwnerOnEnquiry: true,
      leadDigestEnabled: false,
      adminAlertRecipients: "",
    },
    { token },
  );

/**
 * Uploads a branding image (logo / favicon) and returns its public URL.
 * In preview mode it returns a local object URL so the change is still visible.
 */
export async function uploadBrandingImage(
  file: File,
  kind: "logo" | "favicon" | "og",
  token: string | null,
): Promise<string> {
  if (IS_OFFLINE) return URL.createObjectURL(file);

  const form = new FormData();
  form.append("kind", kind);
  form.append("file", file);

  const res = await fetch(apiUrl("/admin/upload"), {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: form,
  });

  const raw = await res.text();
  let parsed: { code?: string; data?: { url?: string }; meta?: { message?: string } } | null = null;
  try {
    parsed = raw ? (JSON.parse(raw) as typeof parsed) : null;
  } catch {
    parsed = null;
  }

  if (!res.ok || (parsed?.code && parsed.code !== "OK")) {
    throw new ApiError(parsed?.meta?.message || "Upload failed. Please try another image.", res.status);
  }
  const url = parsed?.data?.url;
  if (!url) throw new ApiError("The server did not return an image URL.", 500);
  return uploadUrl(url);
}

/**
 * Uploads a listing photo (public listing gallery) from the admin console.
 * Mirrors the owner media upload but posts to the admin-only endpoint and
 * stores the file under /uploads/listings. Throws on failure so callers can
 * surface a clear message — never falls back to a browser object URL.
 */
export async function uploadAdminListingImage(
  file: File,
  token: string | null,
): Promise<string> {
  if (IS_OFFLINE) {
    throw new ApiError("Photo uploads are available on the live site.", 0, "Offline");
  }

  const form = new FormData();
  form.append("file", file);

  const res = await fetch(apiUrl("/admin/listing-upload"), {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: form,
  });

  const raw = await res.text();
  let parsed: { code?: string; data?: { url?: string }; meta?: { message?: string } } | null = null;
  try {
    parsed = raw ? (JSON.parse(raw) as typeof parsed) : null;
  } catch {
    parsed = null;
  }

  if (!res.ok || (parsed?.code && parsed.code !== "OK")) {
    throw new ApiError(parsed?.meta?.message || "Upload failed. Please try another image.", res.status);
  }
  const url = parsed?.data?.url;
  if (!url) throw new ApiError("The server did not return an image URL.", 500);
  return uploadUrl(url);
}

/**
 * Fires `POST path` with a JSON body for admin list actions
 * (approve/reject, feature, pause, suspend, reactivate, …).
 * Resolves to success; never throws so list actions can keep their inline state.
 */
export async function adminAction(
  path: string,
  payload: Record<string, unknown>,
  token: string | null,
): Promise<boolean> {
  if (IS_OFFLINE) {
    await simulate();
    return true;
  }
  try {
    await request(path, { method: "POST", token, body: payload });
    return true;
  } catch {
    return false;
  }
}

/* ---------------- Announcements marquee (home-page ticker) ---------------- */

/** Every marquee message, inactive included, for the admin console. */
export const getAdminMarquee = (token: string | null): Promise<MarqueeMessage[]> =>
  readThrough<MarqueeMessage[]>("/admin/marquee", marqueeMessages as MarqueeMessage[], { token });

/** Payload accepted when creating or updating a marquee message. */
export interface MarqueeMessageInput {
  id?: string;
  message: string;
  linkUrl?: string;
  linkLabel?: string;
  /** Colour preset key (see src/config/marqueeColors.ts). */
  color?: string;
  displayOrder?: number;
  isActive?: boolean;
}

/** Creates (no id) or updates (id present) a marquee message. Throws on failure. */
export async function saveAdminMarqueeMessage(
  input: MarqueeMessageInput,
  token: string | null,
): Promise<void> {
  if (IS_OFFLINE) {
    await simulate();
    return;
  }
  if (input.id) {
    await request(`/admin/marquee/${input.id}`, { method: "PUT", token, body: input });
  } else {
    await request("/admin/marquee", { method: "POST", token, body: input });
  }
}

/** Deletes a marquee message. Resolves to success; never throws. */
export async function deleteAdminMarqueeMessage(
  id: string,
  token: string | null,
): Promise<boolean> {
  if (IS_OFFLINE) {
    await simulate();
    return true;
  }
  try {
    await request(`/admin/marquee/${id}`, { method: "DELETE", token });
    return true;
  } catch {
    return false;
  }
}

/* ---------------- Popup ads (centre-screen lightbox) ---------------- */

/** Every popup ad (enabled or not) for the admin console. */
export const getAdminPopupAds = (token: string | null): Promise<PopupAd[]> =>
  readThrough<PopupAd[]>("/admin/popup-ads", popupAds as unknown as PopupAd[], { token });

/** Payload accepted when creating or updating a popup ad. */
export interface PopupAdInput {
  id?: string;
  title: string;
  mediaType: PopupAdMediaType;
  mediaUrl: string;
  linkUrl?: string;
  linkLabel?: string;
  targetPages: string[];
  frequencyCap?: number;
  delaySeconds?: number;
  startAt?: string;
  endAt?: string;
  isActive?: boolean;
  priority?: number;
}

/** Creates (no id) or updates (id present) a popup ad. Throws on failure. */
export async function saveAdminPopupAd(
  input: PopupAdInput,
  token: string | null,
): Promise<void> {
  if (IS_OFFLINE) {
    await simulate();
    return;
  }
  if (input.id) {
    await request(`/admin/popup-ads/${input.id}`, { method: "PUT", token, body: input });
  } else {
    await request("/admin/popup-ads", { method: "POST", token, body: input });
  }
}

/** Deletes a popup ad. Resolves to success; never throws. */
export async function deleteAdminPopupAd(
  id: string,
  token: string | null,
): Promise<boolean> {
  if (IS_OFFLINE) {
    await simulate();
    return true;
  }
  try {
    await request(`/admin/popup-ads/${id}`, { method: "DELETE", token });
    return true;
  } catch {
    return false;
  }
}

/**
 * Uploads a popup's image or short video and returns its public URL + type.
 * Throws on failure so the editor can surface a clear message.
 */
export async function uploadPopupAdMedia(
  file: File,
  token: string | null,
): Promise<{ url: string; mediaType: PopupAdMediaType }> {
  if (IS_OFFLINE) {
    return {
      url: URL.createObjectURL(file),
      mediaType: file.type.startsWith("video") ? "video" : "image",
    };
  }

  const form = new FormData();
  form.append("file", file);

  const res = await fetch(apiUrl("/admin/popup-ads/upload"), {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: form,
  });

  const raw = await res.text();
  let parsed: {
    code?: string;
    data?: { url?: string; mediaType?: PopupAdMediaType };
    meta?: { message?: string };
  } | null = null;
  try {
    parsed = raw ? (JSON.parse(raw) as typeof parsed) : null;
  } catch {
    parsed = null;
  }

  if (!res.ok || (parsed?.code && parsed.code !== "OK")) {
    throw new ApiError(parsed?.meta?.message || "Upload failed. Please try another file.", res.status);
  }
  const url = parsed?.data?.url;
  if (!url) throw new ApiError("The server did not return a media URL.", 500);
  return { url: uploadUrl(url), mediaType: parsed?.data?.mediaType || "image" };
}

/* ---------------- Taxonomy CRUD (Admin Console → Categories) ---------------- */

/** Fields the admin category editor can set. */
export interface CategoryInput {
  name: string;
  slug?: string;
  icon?: string;
  color?: string;
  description?: string;
  group?: string;
  displayOrder?: number;
  isActive?: boolean;
}

/** Creates a category. Resolves to the created record, or null on failure. */
export async function createAdminCategory(
  input: CategoryInput,
  token: string | null,
): Promise<Category | null> {
  if (IS_OFFLINE) {
    await simulate();
    return {
      id: `cat-${Date.now()}`,
      name: input.name,
      slug: input.slug || "",
      icon: input.icon || "ri-price-tag-3-line",
      description: input.description || "Newly added category",
      businessCount: 0,
      group: input.group,
    };
  }
  try {
    return await request<Category>("/admin/categories", { method: "POST", token, body: input });
  } catch {
    return null;
  }
}

/** Persists edits to a single category. Returns success; never throws. */
export async function updateAdminCategory(
  id: string,
  patch: CategoryInput,
  token: string | null,
): Promise<boolean> {
  if (IS_OFFLINE) {
    await simulate();
    return true;
  }
  try {
    await request(`/admin/categories/${id}`, { method: "PUT", token, body: patch });
    return true;
  } catch {
    return false;
  }
}

/** Deletes a category. Returns success; never throws. */
export async function deleteAdminCategory(id: string, token: string | null): Promise<boolean> {
  if (IS_OFFLINE) {
    await simulate();
    return true;
  }
  try {
    await request(`/admin/categories/${id}`, { method: "DELETE", token });
    return true;
  } catch {
    return false;
  }
}

/* ---------------- Taxonomy CRUD (Admin Console → Subcategories) ---------------- */

/** Fields the admin subcategory editor can set. */
export interface SubcategoryInput {
  name: string;
  slug?: string;
  categoryId: string;
  isActive?: boolean;
}

/** Creates a subcategory. Resolves to the created record, or null on failure. */
export async function createAdminSubcategory(
  input: SubcategoryInput,
  token: string | null,
): Promise<Subcategory | null> {
  if (IS_OFFLINE) {
    await simulate();
    return {
      id: `subc-${Date.now()}`,
      name: input.name,
      slug: input.slug || "",
      categoryId: input.categoryId,
      categoryName: "",
      businessCount: 0,
      status: input.isActive === false ? "hidden" : "active",
    };
  }
  try {
    return await request<Subcategory>("/admin/subcategories", { method: "POST", token, body: input });
  } catch {
    return null;
  }
}

/** Persists edits to a subcategory. Returns success; never throws. */
export async function updateAdminSubcategory(
  id: string,
  patch: Partial<SubcategoryInput>,
  token: string | null,
): Promise<boolean> {
  if (IS_OFFLINE) {
    await simulate();
    return true;
  }
  try {
    await request(`/admin/subcategories/${id}`, { method: "PUT", token, body: patch });
    return true;
  } catch {
    return false;
  }
}

/** Deletes a subcategory. Returns success; never throws. */
export async function deleteAdminSubcategory(id: string, token: string | null): Promise<boolean> {
  if (IS_OFFLINE) {
    await simulate();
    return true;
  }
  try {
    await request(`/admin/subcategories/${id}`, { method: "DELETE", token });
    return true;
  } catch {
    return false;
  }
}

/* ---------------- Locations CRUD (Admin Console → Locations) ---------------- */

/** Offline demo: districts derived from the bundled location mock data. */
const mockAdminDistricts: DistrictOption[] = Array.from(
  new Set((locations as Location[]).map((l) => l.district)),
).map((name, index) => ({
  id: `district-${index + 1}`,
  name,
  slug: name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""),
}));

const mockAdminLocations: AdminLocation[] = (locations as Location[]).map((l) => ({
  id: l.id,
  name: l.name,
  slug: l.slug,
  districtId: mockAdminDistricts.find((d) => d.name === l.district)?.id ?? "",
  districtName: l.district,
  description: l.description,
  image: l.image,
  businessCount: l.businessCount,
  status: "active",
}));

/** Every town/locality, for the admin locations table. */
export const getAdminLocations = (token: string | null): Promise<AdminLocation[]> =>
  readThrough<AdminLocation[]>("/admin/locations", mockAdminLocations, { token });

/** Districts available as a parent for a location. */
export const getAdminDistricts = (token: string | null): Promise<DistrictOption[]> =>
  readThrough<DistrictOption[]>("/admin/districts", mockAdminDistricts, { token });

/** Fields the admin location editor can set. */
export interface LocationInput {
  name: string;
  slug?: string;
  districtId: string;
  pinCode?: string;
  description?: string;
  image?: string;
  isActive?: boolean;
}

/** Creates a location. Resolves to the created record, or null on failure. */
export async function createAdminLocation(
  input: LocationInput,
  token: string | null,
): Promise<AdminLocation | null> {
  if (IS_OFFLINE) {
    await simulate();
    return {
      id: `loc-${Date.now()}`,
      name: input.name,
      slug: input.slug || "",
      districtId: input.districtId,
      districtName: mockAdminDistricts.find((d) => d.id === input.districtId)?.name ?? "",
      description: input.description || "",
      image: input.image || "",
      businessCount: 0,
      status: input.isActive === false ? "hidden" : "active",
    };
  }
  try {
    return await request<AdminLocation>("/admin/locations", { method: "POST", token, body: input });
  } catch {
    return null;
  }
}

/** Persists edits to a location. Returns success; never throws. */
export async function updateAdminLocation(
  id: string,
  patch: Partial<LocationInput>,
  token: string | null,
): Promise<boolean> {
  if (IS_OFFLINE) {
    await simulate();
    return true;
  }
  try {
    await request(`/admin/locations/${id}`, { method: "PUT", token, body: patch });
    return true;
  } catch {
    return false;
  }
}

/** Deletes a location. Returns success; never throws. */
export async function deleteAdminLocation(id: string, token: string | null): Promise<boolean> {
  if (IS_OFFLINE) {
    await simulate();
    return true;
  }
  try {
    await request(`/admin/locations/${id}`, { method: "DELETE", token });
    return true;
  } catch {
    return false;
  }
}

/* ---------------- CRM CRUD (contacts / orders / invoices / subscriptions) ---------------- */

/** Fields the admin contact editor can set. */
export interface ContactInput {
  name: string;
  email?: string;
  phone?: string;
  company?: string;
  source?: string;
  status?: ContactLead["status"];
  tag?: string;
  notes?: string;
}

/** Creates a CRM contact. Resolves to the created record, or null on failure. */
export async function createAdminContact(input: ContactInput, token: string | null): Promise<ContactLead | null> {
  if (IS_OFFLINE) {
    await simulate();
    return { id: `con-${Date.now()}`, source: input.source || "Admin", status: input.status || "new", tag: input.tag || "", notes: input.notes || "", email: input.email || "", phone: input.phone || "", company: input.company || "", createdAt: new Date().toISOString().slice(0, 10), name: input.name };
  }
  try {
    return await request<ContactLead>("/admin/crm/contacts", { method: "POST", token, body: input });
  } catch {
    return null;
  }
}

/** Persists edits to a CRM contact. Returns success; never throws. */
export async function updateAdminContact(id: string, patch: Partial<ContactInput>, token: string | null): Promise<boolean> {
  if (IS_OFFLINE) {
    await simulate();
    return true;
  }
  try {
    await request(`/admin/crm/contacts/${id}`, { method: "PUT", token, body: patch });
    return true;
  } catch {
    return false;
  }
}

/** Deletes a CRM contact. Returns success; never throws. */
export async function deleteAdminContact(id: string, token: string | null): Promise<boolean> {
  if (IS_OFFLINE) {
    await simulate();
    return true;
  }
  try {
    await request(`/admin/crm/contacts/${id}`, { method: "DELETE", token });
    return true;
  } catch {
    return false;
  }
}

/** Deletes many CRM contacts at once. Returns success; never throws. */
export async function bulkDeleteAdminContacts(ids: string[], token: string | null): Promise<boolean> {
  if (IS_OFFLINE) {
    await simulate();
    return true;
  }
  try {
    await request("/admin/crm/contacts/bulk-delete", { method: "POST", token, body: { ids: ids.map(Number) } });
    return true;
  } catch {
    return false;
  }
}

/** Fields the admin order editor can set. */
export interface OrderInput {
  orderNo: string;
  customer?: string;
  planName?: string;
  amount?: number;
  status?: OrderRecord["status"];
  paymentMode?: string;
}

/** Creates an order. Resolves to the created record, or null on failure. */
export async function createAdminOrder(input: OrderInput, token: string | null): Promise<OrderRecord | null> {
  if (IS_OFFLINE) {
    await simulate();
    return { id: `ao-${Date.now()}`, orderNo: input.orderNo, customer: input.customer || "", planName: input.planName || "", amount: input.amount || 0, status: input.status || "pending", createdAt: new Date().toISOString().slice(0, 10) };
  }
  try {
    return await request<OrderRecord>("/admin/crm/orders", { method: "POST", token, body: input });
  } catch {
    return null;
  }
}

/** Persists edits to an order. Returns success; never throws. */
export async function updateAdminOrder(id: string, patch: Partial<OrderInput>, token: string | null): Promise<boolean> {
  if (IS_OFFLINE) {
    await simulate();
    return true;
  }
  try {
    await request(`/admin/crm/orders/${id}`, { method: "PUT", token, body: patch });
    return true;
  } catch {
    return false;
  }
}

/** Deletes an order. Returns success; never throws. */
export async function deleteAdminOrder(id: string, token: string | null): Promise<boolean> {
  if (IS_OFFLINE) {
    await simulate();
    return true;
  }
  try {
    await request(`/admin/crm/orders/${id}`, { method: "DELETE", token });
    return true;
  } catch {
    return false;
  }
}

/** Deletes many orders at once. Returns success; never throws. */
export async function bulkDeleteAdminOrders(ids: string[], token: string | null): Promise<boolean> {
  if (IS_OFFLINE) {
    await simulate();
    return true;
  }
  try {
    await request("/admin/crm/orders/bulk-delete", { method: "POST", token, body: { ids: ids.map(Number) } });
    return true;
  } catch {
    return false;
  }
}

/** Fields the admin invoice editor can set. */
export interface InvoiceInput {
  number: string;
  orderNo?: string;
  amount?: number;
  tax?: number;
  status?: InvoiceRecord["status"];
  issuedAt?: string;
  dueAt?: string;
}

/** Creates an invoice. Resolves to the created record, or null on failure. */
export async function createAdminInvoice(input: InvoiceInput, token: string | null): Promise<InvoiceRecord | null> {
  if (IS_OFFLINE) {
    await simulate();
    return { id: `inv-${Date.now()}`, number: input.number, contactName: "", contactEmail: "", orderNo: input.orderNo || "", amount: input.amount || 0, tax: input.tax || 0, status: input.status || "draft", issuedAt: input.issuedAt || "", dueAt: input.dueAt || "", items: [] };
  }
  try {
    return await request<InvoiceRecord>("/admin/crm/invoices", { method: "POST", token, body: input });
  } catch {
    return null;
  }
}

/** Persists edits to an invoice. Returns success; never throws. */
export async function updateAdminInvoice(id: string, patch: Partial<InvoiceInput>, token: string | null): Promise<boolean> {
  if (IS_OFFLINE) {
    await simulate();
    return true;
  }
  try {
    await request(`/admin/crm/invoices/${id}`, { method: "PUT", token, body: patch });
    return true;
  } catch {
    return false;
  }
}

/** Deletes an invoice. Returns success; never throws. */
export async function deleteAdminInvoice(id: string, token: string | null): Promise<boolean> {
  if (IS_OFFLINE) {
    await simulate();
    return true;
  }
  try {
    await request(`/admin/crm/invoices/${id}`, { method: "DELETE", token });
    return true;
  } catch {
    return false;
  }
}

/** Deletes many invoices at once. Returns success; never throws. */
export async function bulkDeleteAdminInvoices(ids: string[], token: string | null): Promise<boolean> {
  if (IS_OFFLINE) {
    await simulate();
    return true;
  }
  try {
    await request("/admin/crm/invoices/bulk-delete", { method: "POST", token, body: { ids: ids.map(Number) } });
    return true;
  } catch {
    return false;
  }
}

/** Fields the admin subscription editor can set. */
export interface SubscriptionInput {
  plan?: string;
  amount?: number;
  cycle?: Subscription["cycle"];
  renewsAt?: string;
  status?: Subscription["status"];
}

/** Creates a subscription. Resolves to the created record, or null on failure. */
export async function createAdminSubscription(input: SubscriptionInput, token: string | null): Promise<Subscription | null> {
  if (IS_OFFLINE) {
    await simulate();
    return { id: `sub-${Date.now()}`, business: "—", owner: "—", plan: input.plan || "", amount: input.amount || 0, cycle: input.cycle || "Monthly", renewsAt: input.renewsAt || "", status: input.status || "active" };
  }
  try {
    return await request<Subscription>("/admin/subscriptions", { method: "POST", token, body: input });
  } catch {
    return null;
  }
}

/** Persists edits to a subscription. Returns success; never throws. */
export async function updateAdminSubscription(id: string, patch: Partial<SubscriptionInput>, token: string | null): Promise<boolean> {
  if (IS_OFFLINE) {
    await simulate();
    return true;
  }
  try {
    await request(`/admin/subscriptions/${id}`, { method: "PUT", token, body: patch });
    return true;
  } catch {
    return false;
  }
}

/** Deletes a subscription. Returns success; never throws. */
export async function deleteAdminSubscription(id: string, token: string | null): Promise<boolean> {
  if (IS_OFFLINE) {
    await simulate();
    return true;
  }
  try {
    await request(`/admin/subscriptions/${id}`, { method: "DELETE", token });
    return true;
  } catch {
    return false;
  }
}